import { BigintIsh, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { keepPreviousData } from '@tanstack/react-query'
import KodiakFarmABI from 'abis/KodiakFarm.json'
import {
  DEFAULT_WAGMI_REFETCH_POLICY,
  REFETCH_POLICY_FAST,
  RefetchPolicy,
  TANSTACK_REFETCH_POLICY_FAST,
  TANSTACK_REFETCH_POLICY_SLOW,
} from 'config/RefetchPolicies'
import { ISLANDS_TO_FARM } from 'constants/lists'
import { type FarmsQueryParams, useFarmById, useFarms } from 'hooks/backend/farms'
import { useConnection } from 'hooks/useConnection'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FarmFilterGroup } from 'state/filterTypes'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { FarmMemoryFilter, filterNameToStakingTokenType } from 'state/kodiakFarms/filters'
import { endDataTransition, setDataTransition } from 'state/kodiakFarms/reducer'
import { KodiakFarm } from 'types/farms'
import { Abi, Address } from 'viem'
import { useReadContract } from 'wagmi'

import { useToken, useTokens } from './Tokens'
import { useReadContractMultiple } from './useReadContractMultiple'
import { useApiTokenValue, useApiTokenValues } from './useTokenPrice'
export const YEAR = 3600 * 24 * 365

export const useKodiakFarmByPool = (farmAddress?: Address): KodiakFarm | null => {
  const { data: farm, isLoading: isLoadingFarm } = useKodiakFarm(farmAddress)

  if (isLoadingFarm || !farm) return null
  return farm
}

export const useKodiakFarmAddressByIsland = (island?: string) => {
  const { chainId } = useConnection()
  const farmMap = useMemo(() => (chainId ? ISLANDS_TO_FARM[chainId] : {}), [chainId])
  return useMemo(() => (island && farmMap ? farmMap[island.toLowerCase()] : undefined), [island, farmMap])
}

export const useKodiakFarmByIsland = (island?: string) => {
  const { chainId } = useConnection()
  const farmMap = useMemo(() => (chainId ? ISLANDS_TO_FARM[chainId] : {}), [chainId])
  const farmAddress = useMemo(() => (island && farmMap ? farmMap[island.toLowerCase()] : undefined), [island, farmMap])

  const { data: farm, isLoading: isLoadingFarm } = useKodiakFarm(farmAddress as Address)
  if (farmAddress === undefined) {
    return undefined
  }

  return farm
}

export const useCalculationEarnings = ({
  inputValue,
  lockTimeValue,
  multiplier,
  farm,
}: {
  inputValue?: string | undefined | null
  lockTimeValue: number
  multiplier: number
  farm?: KodiakFarm | null
}): (CurrencyAmount<Token> | undefined)[] => {
  const stakingToken = useToken(farm?.stakingToken.id)

  const contracts = useMemo(() => {
    if (!farm?.address) return []
    return [
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getRewardForDuration',
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getAllRewardTokens',
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'totalCombinedWeight',
      },
    ]
  }, [farm?.address])

  const { data: results } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const rewardsForDurationResult = results?.[0]
  const rewardTokensResult = results?.[1]
  const totalCombinedWeightResult = results?.[2]

  const rewardTokensAddresses = useMemo(() => (rewardTokensResult?.result as string[]) || [], [rewardTokensResult])
  const rewardTokens = useTokens(rewardTokensAddresses)

  const rewardsPerDuration = useMemo(() => {
    if (!rewardsForDurationResult?.result || !Array.isArray(rewardsForDurationResult.result)) return []
    return rewardTokens.map((token, index) =>
      token
        ? CurrencyAmount.fromRawAmount(token, (rewardsForDurationResult.result as bigint[])[index]?.toString() || '0')
        : undefined
    )
  }, [rewardsForDurationResult, rewardTokens])

  const totalCombinedWeight = useMemo(
    () => parseFloat(totalCombinedWeightResult?.result?.toString() || '0'),
    [totalCombinedWeightResult]
  )

  return useMemo(() => {
    if (!farm || !stakingToken) return []

    const rewardsDuration = parseFloat(farm.rewardsDuration)
    const perYear = lockTimeValue === 0
    const period = perYear ? YEAR : lockTimeValue

    return rewardTokens.map((rewardToken, index) => {
      const rewardPerDuration = parseFloat(rewardsPerDuration[index]?.toExact() || '0')
      const pendingWeight = parseFloat(inputValue || '0') * 10 ** stakingToken.decimals * multiplier
      const totalWeight = totalCombinedWeight + pendingWeight

      if (totalWeight === 0) return undefined

      const reward = (rewardPerDuration * period * pendingWeight) / (rewardsDuration * totalWeight)
      return tryParseCurrencyAmount(reward.toFixed(rewardToken.decimals), rewardToken)
    })
  }, [farm, stakingToken, rewardTokens, rewardsPerDuration, totalCombinedWeight, inputValue, multiplier, lockTimeValue])
}

export const useKodiakFarmYearEmission = (farm: KodiakFarm | null | undefined) => {
  const contracts = useMemo(() => {
    if (!farm?.address) return []
    return [
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getAllRewardRates',
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getAllRewardTokens',
      },
    ]
  }, [farm?.address])

  const { data: results } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const rewardRatesResult = results?.[0]
  const rewardTokensResult = results?.[1]

  const rewardTokensAddresses = useMemo(() => (rewardTokensResult?.result as string[]) || [], [rewardTokensResult])
  const rewardTokens = useTokens(rewardTokensAddresses)

  const rewardAmounts = useMemo(() => {
    if (!rewardRatesResult?.result || !Array.isArray(rewardRatesResult.result) || !rewardTokens?.length) return null

    return (rewardRatesResult.result as bigint[])
      .map((rate: bigint, index: number) => {
        const token = rewardTokens[index]
        if (!token) return undefined

        try {
          const yearlyAmount = rate * BigInt(YEAR)
          return CurrencyAmount.fromRawAmount(token, yearlyAmount.toString())
        } catch (error) {
          console.error(`Error calculating reward for token ${token.symbol}:`, error)
          return undefined
        }
      })
      .filter(Boolean) as CurrencyAmount<Token>[]
  }, [rewardRatesResult?.result, rewardTokens])

  const { values: tokenValues } = useApiTokenValues(rewardAmounts)

  return useMemo(() => {
    if (!tokenValues?.length) return 0
    return tokenValues.reduce((total, item) => (item ? total + parseFloat(item.toExact()) : total), 0)
  }, [tokenValues])
}

export const useKodiakFarmAverageApr = (farm: KodiakFarm | null | undefined): { apr: number; isLoading: boolean } => {
  const farmAddress = useMemo(() => farm?.address, [farm?.address])
  const _stakingToken = useMemo(() => farm?.stakingToken.id, [farm?.stakingToken.id])

  const emissionUSD = useKodiakFarmYearEmission(farm)
  const stakingToken = useToken(_stakingToken)

  const { data: lockedResult, isLoading } = useReadContract({
    address: farmAddress,
    abi: KodiakFarmABI as Abi,
    functionName: 'totalLiquidityLocked',
    query: {
      enabled: !!farmAddress,
    },
  })

  const locked = useMemo(() => {
    if (!stakingToken || !lockedResult) return undefined
    return CurrencyAmount.fromRawAmount(stakingToken, lockedResult.toString())
  }, [stakingToken, lockedResult])

  const { value: lockedUSD } = useApiTokenValue(locked)

  const apr = useMemo(() => {
    if (isLoading || !lockedUSD || lockedUSD === 0 || emissionUSD === null) return 0
    return Number.isFinite(emissionUSD / lockedUSD) ? (emissionUSD / lockedUSD) * 100 : 0
  }, [isLoading, emissionUSD, lockedUSD])

  return { apr, isLoading }
}

export const useKodiakFarmApr = (farm?: KodiakFarm, multiplier?: number, value?: CurrencyAmount<Token>) => {
  const contracts = useMemo(() => {
    if (!farm?.address) return []
    return [
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getAllRewardTokens',
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'totalCombinedWeight',
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getRewardForDuration',
      },
    ]
  }, [farm?.address])

  const { data: results } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const rewardTokensResult = results?.[0]
  const totalCombinedWeightResult = results?.[1]
  const rewardsForDurationResult = results?.[2]

  const rewardTokensAddresses = useMemo(() => (rewardTokensResult?.result as string[]) || [], [rewardTokensResult])
  const rewardTokens = useTokens(rewardTokensAddresses)

  const totalCombinedWeightString = useMemo(
    () => totalCombinedWeightResult?.result?.toString() || '0',
    [totalCombinedWeightResult]
  )

  const rewardAmounts = useMemo(() => {
    if (!rewardsForDurationResult?.result || !Array.isArray(rewardsForDurationResult.result) || !rewardTokens?.length) {
      return null
    }

    try {
      const rewardValues = rewardsForDurationResult.result as bigint[]
      return rewardTokens
        .map((token, index) => {
          if (!token || index >= rewardValues.length) {
            return undefined
          }
          return CurrencyAmount.fromRawAmount(token, rewardValues[index].toString())
        })
        .filter((amount): amount is CurrencyAmount<Token> => amount !== undefined)
    } catch (error) {
      console.error('Error calculating reward amounts:', error)
      return null
    }
  }, [rewardsForDurationResult?.result, rewardTokens])

  const { values: rewardsPerTokenValues } = useApiTokenValues(rewardAmounts)
  const { value: userValueUSD } = useApiTokenValue(value)

  return useMemo(() => {
    if (
      !farm ||
      userValueUSD == null ||
      totalCombinedWeightString === '0' ||
      userValueUSD === 0 ||
      !rewardsPerTokenValues?.length
    ) {
      return 0
    }

    try {
      const rewardPerTokenValueTotal = rewardsPerTokenValues.reduce(
        (total, item) => (item ? total + parseFloat(item.toExact()) : total),
        0
      )

      if (rewardPerTokenValueTotal === 0) {
        return 0
      }

      const userMultiplier = multiplier || parseFloat(farm.lockMaxMultiplier) / 1e18
      const userWeight = parseFloat(value?.quotient.toString() || '0') * userMultiplier
      const totalCombinedWeightWithUser = parseFloat(totalCombinedWeightString) + userWeight
      const rewardsDuration = parseInt(farm.rewardsDuration)
      const perYear = (rewardPerTokenValueTotal / rewardsDuration) * YEAR

      return (((userWeight / totalCombinedWeightWithUser) * perYear) / userValueUSD) * 100
    } catch (error) {
      console.error('Error calculating APR:', error)
      return 0
    }
  }, [farm, userValueUSD, totalCombinedWeightString, rewardsPerTokenValues, multiplier, value?.quotient])
}

const farmAndAccountRequiredFunction =
  <T>(callback: (farm?: KodiakFarm | null, accountAddress?: Address) => T) =>
  (farm?: KodiakFarm | null, accountAddress?: Address) => {
    const { account: providedAccount } = useConnection()
    const account = useMemo(() => accountAddress || providedAccount, [accountAddress, providedAccount])

    return callback(farm, account)
  }

export type KodiakFarmStake = {
  kekId: string
  liquidity: CurrencyAmount<Token> | undefined
  endingTimestamp: Date
  lockMultiplier: number
}

export const useKodiakFarmEarned = (
  farm?: KodiakFarm | null,
  accountAddress?: Address,
  refetchPolicy: RefetchPolicy = REFETCH_POLICY_FAST
) => {
  const { account: providedAccount } = useConnection()
  const account = useMemo(() => accountAddress || providedAccount, [accountAddress, providedAccount])

  const contracts = useMemo(() => {
    if (!farm?.address || !account) return []
    return [
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'earned',
        args: [account],
      },
      {
        address: farm.address,
        abi: KodiakFarmABI as Abi,
        functionName: 'getAllRewardTokens',
      },
    ]
  }, [farm?.address, account])

  const { data: results, isLoading } = useReadContractMultiple({
    contracts,
    query: {
      ...refetchPolicy,
      enabled: contracts.length > 0,
    },
  })

  const earnedResult = results?.[0]
  const rewardTokensResult = results?.[1]

  const rewardTokensAddresses = useMemo(() => (rewardTokensResult?.result as string[]) || [], [rewardTokensResult])
  const rewardTokens = useTokens(rewardTokensAddresses)

  return useMemo(() => {
    if (isLoading) return null
    if (!earnedResult?.result) return undefined

    try {
      const raws = (earnedResult.result as bigint[]).map((value) => value.toString())

      return raws
        .map((raw, index) => {
          const token = rewardTokens?.[index]
          if (!token) return undefined
          return CurrencyAmount.fromRawAmount(token, raw)
        })
        .filter((amount): amount is CurrencyAmount<Token> => amount !== undefined)
    } catch (error) {
      console.error('Error processing earned rewards:', error)
      return undefined
    }
  }, [earnedResult, rewardTokens, isLoading])
}

export type KodiakFarmStakeResponse = {
  isLoading: boolean
  data:
    | {
        stakes: KodiakFarmStake[] | undefined
        totalLiquidity: CurrencyAmount<Token> | undefined
        totalLiquidityUSD: number | undefined
        totalAvailableLiquidity: CurrencyAmount<Token> | undefined
        avaliableStakes: KodiakFarmStake[] | undefined
        userStakedUSDValue: number | undefined
      }
    | undefined
}

export const useKodiakFarmLockedStakesOf = farmAndAccountRequiredFunction((farm, account) => {
  const {
    data: result,
    isLoading,
    error,
  } = useReadContract({
    address: farm?.address,
    abi: KodiakFarmABI as Abi,
    functionName: 'lockedStakesOf',
    args: account ? [account] : undefined,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!farm?.address && !!account,
    },
  })
  const token = useToken(farm?.stakingToken.id)

  const response = useMemo(() => {
    if (error || !result) {
      return {
        isLoading: false,
        data: undefined,
      }
    }
    if (isLoading) {
      return {
        isLoading: true,
        data: undefined,
      }
    }

    const stakes = result as any[]

    const items = stakes.map((stake: any) => {
      return {
        kekId: stake.kek_id,
        liquidity: token ? CurrencyAmount.fromRawAmount(token, stake.liquidity.toString()) : undefined,
        endingTimestamp: new Date(Number(stake.ending_timestamp) * 1000),
        lockMultiplier: parseFloat(stake.lock_multiplier.toString() || '0') / 1e18,
      }
    }) as KodiakFarmStake[]

    const totalLiquidity = token
      ? items.reduce(
          (memo, item) => (item.liquidity ? memo.add(item.liquidity) : memo),
          CurrencyAmount.fromRawAmount(token, 0)
        )
      : undefined

    const avaliableStakes = items.filter((item) => {
      return item.endingTimestamp.getTime() < Date.now() && item.liquidity && item.liquidity.greaterThan('0')
    })

    const totalAvailableLiquidity = sumCurrencyAmount(avaliableStakes.map((item) => item.liquidity))

    return {
      isLoading: false,
      data: {
        stakes: items,
        totalLiquidity,
        totalLiquidityUSD: 0,
        totalAvailableLiquidity,
        avaliableStakes,
      },
    }
  }, [error, isLoading, result, token])

  const { value: totalLiquidityUSD } = useApiTokenValue(response?.data?.totalLiquidity) || undefined
  if (response && response.data && totalLiquidityUSD) {
    response.data.totalLiquidityUSD = totalLiquidityUSD
  }
  return response
})

export const useLockedPositions = farmAndAccountRequiredFunction((farm, account) => {
  const { data: raw } = useReadContract({
    address: farm?.address,
    abi: KodiakFarmABI as Abi,
    functionName: 'lockedStakesOf',
    args: account ? [account] : undefined,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!farm?.address && !!account,
    },
  })

  const stakingToken = useToken(farm?.stakingToken.id)

  return useMemo(() => {
    if (raw && Array.isArray(raw) && raw.length > 0 && stakingToken) {
      return (raw as any[]).map(
        (item: { kek_id: string; liquidity: BigintIsh; ending_timestamp: BigintIsh; lock_multiplier: BigintIsh }) => ({
          kekId: item.kek_id,
          liquidity: CurrencyAmount.fromRawAmount(stakingToken, item.liquidity.toString()),
          endingTimestamp: new Date(Number(item.ending_timestamp) * 1000),
          lockMultiplier: parseFloat(item.lock_multiplier.toString() || '0') / 1e18,
        })
      )
    }
    return []
  }, [raw, stakingToken])
})

export const useLockedAmountOfAccount = farmAndAccountRequiredFunction((farm, account) => {
  const stakingToken = useToken(farm?.stakingToken.id)
  const {
    data: rawLocked,
    error,
    isLoading: loading,
  } = useReadContract({
    address: farm?.address,
    abi: KodiakFarmABI as Abi,
    functionName: 'lockedLiquidityOf',
    args: account ? [account] : undefined,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!farm?.address && !!account,
    },
  })

  return useMemo(() => {
    if (loading) return null
    if (error || !stakingToken) return undefined

    return CurrencyAmount.fromRawAmount(stakingToken, rawLocked?.toString() || '0')
  }, [stakingToken, rawLocked, loading, error])
})

export const useKodiakFarmTotalLocked = (farm: KodiakFarm | undefined, farmAddress?: Address) => {
  const { data: total_liquidity_locked, isLoading } = useReadContract({
    address: farmAddress || farm?.address,
    abi: KodiakFarmABI as Abi,
    functionName: 'totalLiquidityLocked',
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!(farmAddress || farm?.address),
    },
  })
  const token = useToken(farm?.stakingToken.id)

  const tokenCurrency = token
    ? CurrencyAmount.fromRawAmount(token, total_liquidity_locked?.toString() || '0')
    : undefined

  const { value: totalLiquidityUSD } = useApiTokenValue(tokenCurrency) || undefined

  return {
    totalLiquidityLocked: total_liquidity_locked,
    totalLiquidityUSD,
    isLoading,
  }
}

export interface FarmsQueryResponse {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  data: KodiakFarm[]
  totalCount: number
  refetch: () => void
}

export interface FarmsListResponse {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  data: KodiakFarm[]
  refetch: () => void
  filteredFarms: KodiakFarm[]
  isDataTransition: boolean
  transitionType: 'filter' | 'sort' | 'search' | null
  error: any
  pagination: {
    limit: number
    offset: number
    goToPage: (page: number) => void
    currentPage: number
    totalPages: number
  }
}

/**
 * Hook to fetch and sort farms with pagination, sorting, and polling
 */
export function useKodiakFarms(filters: FarmMemoryFilter[], params: FarmsQueryParams = {}): FarmsListResponse {
  const { orderBy = 'tvl', orderDirection = 'desc', provider = 'kodiak', blacklist = [] } = params
  const dispatch = useAppDispatch()

  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
  })

  const { dataTransition, transitionType, searchTerm } = useAppSelector((state) => state.kodiakFarms)
  const [isPaging, setIsPaging] = useState(false)

  const isInitialRenderRef = useRef(true)
  const lastSortRef = useRef({ orderBy, orderDirection })
  const serializedFilters = useMemo(
    () => JSON.stringify(filters.map((f) => ({ name: f.displayName, active: f.isActive }))),
    [filters]
  )
  const lastFiltersRef = useRef(serializedFilters)
  const lastSearchTermRef = useRef(searchTerm)

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }

    const sortChanged = lastSortRef.current.orderBy !== orderBy || lastSortRef.current.orderDirection !== orderDirection

    if (sortChanged) {
      dispatch(setDataTransition({ inTransition: true, type: 'sort' }))
      if (pagination.offset > 0) {
        setPagination((prev) => ({ ...prev, offset: 0 }))
      }
    }

    lastSortRef.current = { orderBy, orderDirection }
  }, [pagination.offset, orderBy, orderDirection, dispatch])

  useEffect(() => {
    if (isInitialRenderRef.current) {
      return
    }

    const filtersChanged = serializedFilters !== lastFiltersRef.current

    if (filtersChanged) {
      dispatch(setDataTransition({ inTransition: true, type: 'filter' }))
      if (pagination.offset > 0) {
        setPagination((prev) => ({ ...prev, offset: 0 }))
      }
    }

    lastFiltersRef.current = serializedFilters
  }, [serializedFilters, pagination.offset, dispatch])

  useEffect(() => {
    if (isInitialRenderRef.current) {
      return
    }

    const searchChanged = lastSearchTermRef.current !== searchTerm

    if (searchChanged) {
      dispatch(setDataTransition({ inTransition: true, type: 'search' }))
      if (pagination.offset > 0) {
        setPagination((prev) => ({ ...prev, offset: 0 }))
      }
    }

    lastSearchTermRef.current = searchTerm
  }, [searchTerm, pagination.offset, dispatch])

  const filterParams = useMemo(() => {
    const activeStakingTokenTypes = filters
      .filter((f) => f.group === FarmFilterGroup.StakingTokenType && f.isActive)
      .map((f) => filterNameToStakingTokenType[f.displayName])
      .filter(Boolean)

    return {
      activeStakingTokenTypes,
      activeOnly: filters.some(
        (f) => f.group === FarmFilterGroup.Active && f.displayName === 'Active Only' && f.isActive
      ),
      hideLowTvl: filters.some(
        (f) => f.group === FarmFilterGroup.LowTvl && f.displayName === 'Hide Low TVL' && f.isActive
      ),
    }
  }, [filters])

  const apiParams = useMemo(() => {
    const params: FarmsQueryParams = {
      provider,
      limit: pagination.limit,
      offset: pagination.offset,
      orderBy,
      orderDirection,
      blacklist,
    }

    if (filterParams.hideLowTvl) {
      params.minimumFarmTvl = 100
    }

    if (filterParams.activeStakingTokenTypes.length > 0) {
      params.type = filterParams.activeStakingTokenTypes
    }

    if (filterParams.activeOnly) {
      params.activeOnly = true
    }

    if (searchTerm && searchTerm.trim().length > 0) {
      params.search = searchTerm.trim()
    }

    return params
  }, [
    provider,
    pagination.limit,
    pagination.offset,
    orderBy,
    orderDirection,
    blacklist,
    filterParams.hideLowTvl,
    filterParams.activeStakingTokenTypes,
    filterParams.activeOnly,
    searchTerm,
  ])

  const {
    data: apiData,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
    refetch,
  } = useFarms(apiParams, {
    ...TANSTACK_REFETCH_POLICY_FAST,
    placeholderData: keepPreviousData,
  })

  const isUninitialized = false
  const rawFarms = apiData?.data || []
  const count = apiData?.count || 0
  const currentData = apiData

  const isCacheHit = isSuccess && !isLoading && !isFetching && !isUninitialized

  // Update pagination total
  useEffect(() => {
    if (isSuccess) {
      setPagination((prev) => ({ ...prev, total: count }))

      if (isPaging) {
        setIsPaging(false)
      }

      if (dataTransition) {
        if (isCacheHit) {
          dispatch(endDataTransition())
        } else if (!isFetching) {
          dispatch(endDataTransition())
        }
      }
    }

    if (isError && dataTransition) {
      dispatch(endDataTransition())
    }
  }, [isSuccess, count, isPaging, dataTransition, isCacheHit, isFetching, isError, dispatch])

  useEffect(() => {
    if (dataTransition) {
      const transitionTimeout = setTimeout(() => {
        dispatch(endDataTransition())
      }, 3000)

      return () => clearTimeout(transitionTimeout)
    }
    return
  }, [dataTransition, dispatch])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pagination.total / pagination.limit)),
    [pagination.total, pagination.limit]
  )

  const currentPage = useMemo(
    () => Math.floor(pagination.offset / pagination.limit),
    [pagination.offset, pagination.limit]
  )

  const goToPage = useCallback((page: number) => {
    setIsPaging(true)
    setPagination((prev) => ({
      ...prev,
      offset: page * prev.limit,
    }))
  }, [])

  const dataToDisplay = useMemo(() => {
    if (dataTransition && !isPaging) {
      return []
    }
    return rawFarms.length > 0 ? rawFarms : currentData?.data || []
  }, [rawFarms, currentData, dataTransition, isPaging])

  const isLoadingState = useMemo(() => {
    return (isLoading && !isError) || (dataTransition && !isPaging)
  }, [isLoading, isError, dataTransition, isPaging])

  return {
    isLoading: isLoadingState,
    isFetching,
    isUninitialized,
    isError,
    data: dataToDisplay,
    refetch,
    filteredFarms: dataToDisplay,
    isDataTransition: dataTransition,
    transitionType,
    error,
    pagination: {
      limit: pagination.limit,
      offset: pagination.offset,
      goToPage,
      currentPage,
      totalPages,
    },
  }
}
/**
 * Hook to fetch a single farm by ID
 */
export interface FarmQueryResponse {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  error?: any
  data: KodiakFarm | null
  refetch: () => void
}

export function useKodiakFarm(id: Address | null | undefined): FarmQueryResponse {
  const [suppress, setSuppress] = useState(false)

  const isQueryDisabled = suppress || !id

  const { data, isFetching, isPending, isError, error, refetch } = useFarmById(id, {
    skip: isQueryDisabled,
    ...(!suppress ? DEFAULT_WAGMI_REFETCH_POLICY : { refetchInterval: false }),
    refetchOnWindowFocus: true,
    refetchOnReconnect: !suppress,
    staleTime: 120 * 1000,
  })

  useEffect(() => {
    const status = (error as any)?.status ?? (error as any)?.originalStatus
    if (isError && status === 404) {
      setSuppress(true)
    }
  }, [isError, error])

  const retry = () => {
    setSuppress(false)
    refetch()
  }

  const isUninitialized = isQueryDisabled
  const isLoading = !data && !isQueryDisabled && isPending

  return {
    isLoading,
    isFetching,
    isUninitialized,
    isError,
    error,
    data: data ?? null,
    refetch: retry,
  }
}

export const useTotalFarmTvl = () => {
  const { data: allFarmsData } = useFarms(
    {
      provider: 'kodiak',
      limit: 100,
      offset: 0,
    },
    {
      ...TANSTACK_REFETCH_POLICY_SLOW,
      placeholderData: keepPreviousData,
    }
  )

  return useMemo(() => {
    if (!allFarmsData?.data) {
      return {
        totalFarmTvl: 0,
        totalFarms: 0,
        activeFarms: 0,
        isLoading: true,
      }
    }

    const farms = allFarmsData.data
    const totalFarmTvl = farms.reduce((sum, farm) => sum + (farm.tvl || 0), 0)
    const totalFarms = farms.length
    const activeFarms = farms.filter((farm) => farm.isActive && !farm.isPaused && !farm.stakingPaused).length

    return {
      totalFarmTvl,
      totalFarms,
      activeFarms,
      isLoading: false,
    }
  }, [allFarmsData])
}
